import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Fade,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory, useLocation, useParams } from "react-router-dom";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CloseIcon from "@material-ui/icons/Close";
import ImageIcon from "@material-ui/icons/Image";
import VideocamIcon from "@material-ui/icons/Videocam";
import NotesIcon from "@material-ui/icons/Notes";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ZoomOutMapIcon from "@material-ui/icons/ZoomOutMap";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import RefreshIcon from "@material-ui/icons/Refresh";
import SchoolIcon from "@material-ui/icons/School";
import LayersOutlinedIcon from "@material-ui/icons/LayersOutlined";
import CollectionsIcon from "@material-ui/icons/Collections";
import ActivityTabelService from "./Locality/Service/activityTabelService";
import SubActivityService from "./Locality/Service/subActivityService";

const asUrlList = (value) =>
  (Array.isArray(value) ? value : value ? [value] : [])
    .map((u) => String(u || "").trim())
    .filter(Boolean);

const hasCatalogContent = (row) => {
  if (!row) return false;
  if (String(row.notes || "").trim()) return true;
  if (asUrlList(row.imageURL).length) return true;
  if (asUrlList(row.videoURL).length) return true;
  return false;
};

const normalizeGalleryRecord = (res, isExercise) => {
  if (!res) return null;
  if (!isExercise) return res;
  return {
    ...res,
    activityName: res.subActivityName || res.activityName || "Exercise",
    notes: res.notes || "",
    imageURL: Array.isArray(res.imageURL) ? res.imageURL : [],
    videoURL: Array.isArray(res.videoURL) ? res.videoURL : [],
    classId: res.classId || res.activityId?.classId || null,
    superActivityId: res.activityId?.superActivityId || res.superActivityId || null,
    parentActivityName: res.activityId?.activityName || "",
    isExercise: true,
  };
};

/** When list-of-activity itself has no notes/media, surface content from exercises. */
const mergeActivityWithExerciseContent = (activity, exercises) => {
  if (!activity) return null;
  if (hasCatalogContent(activity)) return activity;
  const rows = (Array.isArray(exercises) ? exercises : []).filter(hasCatalogContent);
  if (!rows.length) return activity;
  if (rows.length === 1) {
    const only = rows[0];
    return {
      ...activity,
      notes: only.notes || "",
      imageURL: asUrlList(only.imageURL),
      videoURL: asUrlList(only.videoURL),
      contentFromExerciseName: only.subActivityName || "",
    };
  }
  const notes = rows
    .map((r) => {
      const body = String(r.notes || "").trim();
      if (!body) return "";
      const label = String(r.subActivityName || "Exercise").trim();
      return label ? `${label}\n${body}` : body;
    })
    .filter(Boolean)
    .join("\n\n");
  return {
    ...activity,
    notes,
    imageURL: rows.flatMap((r) => asUrlList(r.imageURL)),
    videoURL: rows.flatMap((r) => asUrlList(r.videoURL)),
    contentFromExerciseName: "Exercises",
  };
};

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: "calc(100vh - 100px)",
    borderRadius: 22,
    overflow: "hidden",
    background:
      "radial-gradient(1200px 420px at 12% -10%, rgba(23,105,214,0.16), transparent 60%), radial-gradient(900px 380px at 100% 0%, rgba(15,59,98,0.12), transparent 55%), linear-gradient(180deg, #f3f7fc 0%, #f8fafc 48%, #ffffff 100%)",
  },
  topBar: {
    position: "sticky",
    top: 0,
    zIndex: 5,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2.5),
    backdropFilter: "blur(10px)",
    backgroundColor: "rgba(255,255,255,0.82)",
    borderBottom: "1px solid rgba(228,235,244,0.9)",
  },
  topBarLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
  },
  backBtn: {
    backgroundColor: "#eef4ff",
    color: "#1769d6",
    "&:hover": { backgroundColor: "#e0ebff" },
  },
  topTitle: {
    fontWeight: 800,
    color: "#0f2d4d",
    fontSize: "1.05rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  topSub: {
    color: "#7a8aa0",
    fontSize: "0.78rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  hero: {
    position: "relative",
    margin: theme.spacing(1.5, 2.5, 0),
    borderRadius: 14,
    overflow: "hidden",
    minHeight: 112,
    background: "linear-gradient(135deg, #0f3b62 0%, #1769d6 52%, #1a7fe8 100%)",
    color: "#fff",
    boxShadow: "0 10px 24px rgba(15, 59, 98, 0.16)",
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(1.25, 1.5, 0),
      minHeight: 100,
    },
  },
  heroMedia: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    opacity: 0.35,
  },
  heroShade: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(100deg, rgba(10,30,52,0.88) 8%, rgba(15,59,98,0.55) 48%, rgba(23,105,214,0.25) 100%)",
  },
  heroContent: {
    position: "relative",
    zIndex: 1,
    padding: theme.spacing(1.5, 2.25, 1.5),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.25, 1.5),
    },
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.65rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.9,
    marginBottom: 4,
  },
  title: {
    fontWeight: 800,
    fontSize: "1.35rem",
    lineHeight: 1.2,
    maxWidth: 760,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.15rem",
    },
  },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: theme.spacing(1),
  },
  metaChip: {
    backgroundColor: "rgba(255,255,255,0.16)",
    color: "#fff",
    fontWeight: 600,
    height: 24,
    backdropFilter: "blur(4px)",
    fontSize: "0.72rem",
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
    margin: theme.spacing(2.5),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
      margin: theme.spacing(1.5),
    },
  },
  statCard: {
    borderRadius: 16,
    border: "1px solid #e4ebf4",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    padding: theme.spacing(2),
    boxShadow: "0 8px 22px rgba(15, 59, 98, 0.05)",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  statIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #e8f1fb, #d6e8ff)",
    color: "#1769d6",
    flexShrink: 0,
  },
  statValue: {
    fontWeight: 800,
    fontSize: "1.35rem",
    color: "#0f2d4d",
    lineHeight: 1.1,
  },
  statLabel: {
    color: "#7a8aa0",
    fontSize: "0.78rem",
    fontWeight: 600,
  },
  body: {
    padding: theme.spacing(0, 2.5, 4),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0, 1.5, 3),
    },
  },
  tabsWrap: {
    borderRadius: 14,
    backgroundColor: "#fff",
    border: "1px solid #e4ebf4",
    marginBottom: theme.spacing(2.5),
    overflow: "hidden",
  },
  tabs: {
    minHeight: 48,
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 700,
      minHeight: 48,
      color: "#64748b",
    },
    "& .Mui-selected": {
      color: "#1769d6",
    },
    "& .MuiTabs-indicator": {
      height: 3,
      borderRadius: "3px 3px 0 0",
      backgroundColor: "#1769d6",
    },
  },
  section: {
    marginBottom: theme.spacing(3.5),
  },
  sectionHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1.75),
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontWeight: 800,
    fontSize: "1.1rem",
    color: "#0f2d4d",
  },
  sectionIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #e8f1fb, #d6e8ff)",
    color: "#1769d6",
  },
  countPill: {
    backgroundColor: "#eef4ff",
    color: "#1769d6",
    fontWeight: 700,
    height: 26,
  },
  notesCard: {
    borderRadius: 16,
    border: "1px solid #e4ebf4",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    padding: theme.spacing(2.5),
    boxShadow: "0 8px 24px rgba(15, 59, 98, 0.06)",
    whiteSpace: "pre-wrap",
    color: "#334155",
    lineHeight: 1.75,
    fontSize: "0.95rem",
  },
  imageGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
    gap: 14,
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: 10,
    },
  },
  imageCard: {
    position: "relative",
    borderRadius: 16,
    overflow: "hidden",
    aspectRatio: "1 / 1",
    cursor: "pointer",
    border: "1px solid #e4ebf4",
    backgroundColor: "#dbe7f5",
    boxShadow: "0 10px 28px rgba(15, 59, 98, 0.08)",
    transition: "transform 200ms ease, box-shadow 200ms ease",
    animation: "$fadeUp 420ms ease both",
    "&:hover": {
      transform: "translateY(-5px) scale(1.01)",
      boxShadow: "0 18px 40px rgba(15, 59, 98, 0.16)",
    },
    "&:hover $imageOverlay": { opacity: 1 },
    "&:focus-visible": {
      outline: "2px solid #1769d6",
      outlineOffset: 2,
    },
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  imageOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(180deg, transparent 40%, rgba(15,43,74,0.62) 100%)",
    opacity: 0,
    transition: "opacity 180ms ease",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    padding: 10,
    color: "#fff",
  },
  videoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 16,
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  videoCard: {
    borderRadius: 16,
    overflow: "hidden",
    border: "1px solid #e4ebf4",
    backgroundColor: "#0f2d4d",
    boxShadow: "0 10px 28px rgba(15, 59, 98, 0.1)",
    animation: "$fadeUp 420ms ease both",
  },
  video: {
    width: "100%",
    display: "block",
    maxHeight: 300,
    backgroundColor: "#0f2d4d",
  },
  emptyState: {
    borderRadius: 16,
    border: "1px dashed #c5d4e8",
    backgroundColor: "#f8fbff",
    padding: theme.spacing(5, 3),
    textAlign: "center",
    color: "#64748b",
  },
  emptyIcon: {
    width: 58,
    height: 58,
    borderRadius: 16,
    margin: "0 auto 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#e8f1fb",
    color: "#1769d6",
  },
  loadingWrap: {
    minHeight: 420,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 12,
  },
  lightboxRoot: {
    "& .MuiBackdrop-root": {
      backgroundColor: "rgba(8, 18, 32, 0.88)",
      backdropFilter: "blur(6px)",
    },
  },
  lightboxPaper: {
    backgroundColor: "transparent",
    boxShadow: "none",
    maxWidth: "94vw",
    overflow: "visible",
  },
  lightboxInner: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 14,
  },
  lightboxStage: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "62vh",
    width: "100%",
  },
  lightboxImg: {
    maxWidth: "86vw",
    maxHeight: "68vh",
    borderRadius: 14,
    objectFit: "contain",
    boxShadow: "0 28px 70px rgba(0,0,0,0.5)",
    backgroundColor: "#0b1f33",
  },
  lightboxClose: {
    position: "absolute",
    top: -10,
    right: -10,
    zIndex: 3,
    backgroundColor: "rgba(255,255,255,0.95)",
    "&:hover": { backgroundColor: "#fff" },
  },
  lightboxNav: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 3,
    backgroundColor: "rgba(255,255,255,0.94)",
    "&:hover": { backgroundColor: "#fff" },
  },
  lightboxMeta: {
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.9rem",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  filmstrip: {
    display: "flex",
    gap: 8,
    maxWidth: "86vw",
    overflowX: "auto",
    paddingBottom: 4,
  },
  filmThumb: {
    width: 64,
    height: 48,
    borderRadius: 8,
    objectFit: "cover",
    cursor: "pointer",
    border: "2px solid transparent",
    opacity: 0.7,
    flexShrink: 0,
  },
  filmThumbActive: {
    borderColor: "#5aa2ff",
    opacity: 1,
  },
  hint: {
    color: "rgba(255,255,255,0.7)",
    fontSize: "0.75rem",
  },
  "@keyframes fadeUp": {
    from: { opacity: 0, transform: "translateY(10px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
}));

const EmptyBlock = ({ classes, icon, title, subtitle }) => (
  <Box className={classes.emptyState}>
    <Box className={classes.emptyIcon}>{icon}</Box>
    <Typography style={{ fontWeight: 800, color: "#0f2d4d", marginBottom: 4 }}>{title}</Typography>
    <Typography variant="body2">{subtitle}</Typography>
  </Box>
);

export default function ActivityMediaGallery() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const isExercise = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("type") === "exercise";
  }, [location.search]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activity, setActivity] = useState(null);
  const [tab, setTab] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const loadActivity = useCallback(async () => {
    if (!id) {
      setError(isExercise ? "Exercise id is missing." : "Activity id is missing.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      if (isExercise) {
        const res = await SubActivityService.getSubActivityById(id);
        const normalized = normalizeGalleryRecord(res, true);
        setActivity(normalized);
        if (!normalized) setError("Exercise not found.");
        return;
      }

      const res = await ActivityTabelService.getActivityTabelById(id);
      if (!res) {
        setActivity(null);
        setError("Activity not found.");
        return;
      }

      let resolved = res;
      if (!hasCatalogContent(res)) {
        const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
        const schooleId =
          userDetails?.schooleId?._id ||
          userDetails?.schooleId ||
          userDetails?.schoolId?._id ||
          userDetails?.schoolId ||
          "";
        // Do not filter by classId here — exercise rows may be shared/global for the activity.
        const exercises = await SubActivityService.getAllSubActivityByActivityId({
          activityId: id,
          schooleId: schooleId || undefined,
        });
        resolved = mergeActivityWithExerciseContent(
          res,
          Array.isArray(exercises) ? exercises : []
        );
      }
      setActivity(resolved);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          (isExercise ? "Failed to load exercise media." : "Failed to load activity media.")
      );
      setActivity(null);
    } finally {
      setLoading(false);
    }
  }, [id, isExercise]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  const images = useMemo(() => asUrlList(activity?.imageURL), [activity]);
  const videos = useMemo(() => asUrlList(activity?.videoURL), [activity]);
  const notes = String(activity?.notes || "").trim();
  const title = activity?.activityName || (isExercise ? "Exercise media" : "Activity media");
  const className = activity?.classId?.className || "";
  const areaName = activity?.superActivityId?.superActivityName || "";
  const parentActivityName = activity?.parentActivityName || "";
  const contentFromExerciseName = activity?.contentFromExerciseName || "";
  const cover = images[0] || "";
  const totalMedia = images.length + videos.length;

  useEffect(() => {
    if (lightboxIndex == null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowLeft") {
        setLightboxIndex((idx) =>
          idx == null || !images.length ? idx : (idx + images.length - 1) % images.length
        );
      }
      if (e.key === "ArrowRight") {
        setLightboxIndex((idx) =>
          idx == null || !images.length ? idx : (idx + 1) % images.length
        );
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, images.length]);

  const showPrev = () => {
    if (lightboxIndex == null || !images.length) return;
    setLightboxIndex((lightboxIndex + images.length - 1) % images.length);
  };
  const showNext = () => {
    if (lightboxIndex == null || !images.length) return;
    setLightboxIndex((lightboxIndex + 1) % images.length);
  };

  const renderImages = () =>
    images.length === 0 ? (
      <EmptyBlock
        classes={classes}
        icon={<ImageIcon />}
        title="No images yet"
        subtitle={
          isExercise
            ? "Upload images from Activity Catalogue → Exercises."
            : "Upload images from Activity Catalogue → List Of Activities."
        }
      />
    ) : (
      <Box className={classes.imageGrid}>
        {images.map((url, index) => (
          <Box
            key={`${url}-${index}`}
            className={classes.imageCard}
            style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}
            onClick={() => setLightboxIndex(index)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setLightboxIndex(index);
            }}
          >
            <img src={url} alt={`${title} ${index + 1}`} className={classes.image} loading="lazy" />
            <Box className={classes.imageOverlay}>
              <Typography variant="caption" style={{ fontWeight: 700 }}>
                {index + 1}/{images.length}
              </Typography>
              <ZoomOutMapIcon fontSize="small" />
            </Box>
          </Box>
        ))}
      </Box>
    );

  const renderVideos = () =>
    videos.length === 0 ? (
      <EmptyBlock
        classes={classes}
        icon={<VideocamIcon />}
        title="No videos yet"
        subtitle={
          isExercise
            ? "Upload videos from Activity Catalogue → Exercises."
            : "Upload videos from Activity Catalogue → List Of Activities."
        }
      />
    ) : (
      <Box className={classes.videoGrid}>
        {videos.map((url, index) => (
          <Box
            key={`${url}-${index}`}
            className={classes.videoCard}
            style={{ animationDelay: `${Math.min(index, 8) * 50}ms` }}
          >
            <video src={url} className={classes.video} controls preload="metadata" />
          </Box>
        ))}
      </Box>
    );

  if (loading) {
    return (
      <Box className={classes.page}>
        <Box className={classes.loadingWrap}>
          <CircularProgress style={{ color: "#1769d6" }} />
          <Typography style={{ color: "#64748b", fontWeight: 600 }}>
            {isExercise ? "Preparing exercise media…" : "Preparing activity media…"}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.page}>
      <Box className={classes.topBar}>
        <Box className={classes.topBarLeft}>
          <IconButton className={classes.backBtn} onClick={() => history.goBack()} aria-label="Back">
            <ArrowBackIcon />
          </IconButton>
       
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadActivity}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box className={classes.hero}>
        {cover ? <img src={cover} alt="" className={classes.heroMedia} /> : null}
        <Box className={classes.heroShade} />
        <Box className={classes.heroContent}>
          <Typography className={classes.eyebrow}>
            <CollectionsIcon style={{ fontSize: 16 }} />{" "}
            {isExercise ? "Exercise media" : "Activity media"}
          </Typography>
          <Typography className={classes.title}>{title}</Typography>
          <Box className={classes.metaRow}>
            {areaName && (
              <Chip
                className={classes.metaChip}
                size="small"
                icon={<LayersOutlinedIcon style={{ color: "#fff" }} />}
                label={areaName}
              />
            )}
            {parentActivityName && (
              <Chip className={classes.metaChip} size="small" label={parentActivityName} />
            )}
            {className && (
              <Chip
                className={classes.metaChip}
                size="small"
                icon={<SchoolIcon style={{ color: "#fff" }} />}
                label={className}
              />
            )}
            <Chip className={classes.metaChip} size="small" label={`${totalMedia} media files`} />
          </Box>
        </Box>
      </Box>

  

      <Box className={classes.body}>
        {error && (
          <Box className={classes.emptyState} style={{ marginBottom: 20 }}>
            <Typography style={{ fontWeight: 700, color: "#b91c1c", marginBottom: 10 }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              style={{ backgroundColor: "#1769d6", color: "#fff", textTransform: "none" }}
              onClick={loadActivity}
            >
              Retry
            </Button>
          </Box>
        )}

        <Box className={classes.tabsWrap}>
          <Tabs
            value={tab}
            onChange={(_, next) => setTab(next)}
            className={classes.tabs}
            variant="fullWidth"
          >
            <Tab label="Explanation" />
            <Tab label={`Images (${images.length})`} />
            <Tab label={`Videos (${videos.length})`} />
          </Tabs>
        </Box>

        <Fade in key={tab} timeout={220}>
          <Box>
            {tab === 0 && (
              <Box className={classes.section}>
                <Box className={classes.sectionHead}>
                  <Typography className={classes.sectionTitle}>
                    <span className={classes.sectionIcon}><NotesIcon fontSize="small" /></span>
                    Explanation
                  </Typography>
                </Box>
                {notes ? (
                  <Box className={classes.notesCard}>{notes}</Box>
                ) : (
                  <EmptyBlock
                    classes={classes}
                    icon={<NotesIcon />}
                    title="No explanation yet"
                    subtitle={
                      isExercise
                        ? "Notes added for this exercise in Activity Catalogue will appear here."
                        : "Notes added for this activity in Activity Catalogue will appear here."
                    }
                  />
                )}
              </Box>
            )}

            {tab === 1 && (
              <Box className={classes.section}>
                <Box className={classes.sectionHead}>
                  <Typography className={classes.sectionTitle}>
                    <span className={classes.sectionIcon}><ImageIcon fontSize="small" /></span>
                    Images
                  </Typography>
                  <Chip size="small" className={classes.countPill} label={images.length} />
                </Box>
                {renderImages()}
              </Box>
            )}

            {tab === 2 && (
              <Box className={classes.section}>
                <Box className={classes.sectionHead}>
                  <Typography className={classes.sectionTitle}>
                    <span className={classes.sectionIcon}><VideocamIcon fontSize="small" /></span>
                    Videos
                  </Typography>
                  <Chip size="small" className={classes.countPill} label={videos.length} />
                </Box>
                {renderVideos()}
              </Box>
            )}
          </Box>
        </Fade>
      </Box>

      <Dialog
        open={lightboxIndex != null}
        onClose={() => setLightboxIndex(null)}
        maxWidth={false}
        className={classes.lightboxRoot}
        PaperProps={{ className: classes.lightboxPaper }}
      >
        {lightboxIndex != null && images[lightboxIndex] && (
          <Box className={classes.lightboxInner}>
            <Box className={classes.lightboxStage}>
              <IconButton
                className={classes.lightboxClose}
                onClick={() => setLightboxIndex(null)}
              >
                <CloseIcon />
              </IconButton>
              {images.length > 1 && (
                <IconButton
                  className={classes.lightboxNav}
                  style={{ left: 4 }}
                  onClick={showPrev}
                >
                  <ChevronLeftIcon />
                </IconButton>
              )}
              <img
                src={images[lightboxIndex]}
                alt={`${title} ${lightboxIndex + 1}`}
                className={classes.lightboxImg}
              />
              {images.length > 1 && (
                <IconButton
                  className={classes.lightboxNav}
                  style={{ right: 4 }}
                  onClick={showNext}
                >
                  <ChevronRightIcon />
                </IconButton>
              )}
            </Box>
            <Box className={classes.lightboxMeta}>
              <span>
                {lightboxIndex + 1} / {images.length}
              </span>
              <Tooltip title="Open original">
                <IconButton
                  size="small"
                  style={{ color: "#fff" }}
                  component="a"
                  href={images[lightboxIndex]}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {images.length > 1 && (
              <Box className={classes.filmstrip}>
                {images.map((url, index) => (
                  <img
                    key={`thumb-${index}`}
                    src={url}
                    alt=""
                    className={`${classes.filmThumb} ${
                      index === lightboxIndex ? classes.filmThumbActive : ""
                    }`}
                    onClick={() => setLightboxIndex(index)}
                  />
                ))}
              </Box>
            )}
            <Typography className={classes.hint}>
              Use ← → keys to browse · Esc to close
            </Typography>
          </Box>
        )}
      </Dialog>
    </Box>
  );
}
